// frontend/src/components/DocumentViewer/ImageView.tsx

import React, { useEffect, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import ViewerControls from "./ViewerControls";
import FallbackPreview from "./FallbackPreview";
import LoadingSpinner from "../ui/LoadingSpinner";

interface ImageViewProps {
  url: string;
  documentId: number;
  onFullscreen?: () => void;
  showControls?: boolean;
  className?: string;
  isFullscreenMode?: boolean;
  onLoadComplete?: () => void;
  onLoadError?: (error: Error) => void;
  canDownload?: boolean;
}

export interface ImageViewRef {
  handleRotate: () => void;
  handleResetTransform: () => void;
  handleDownload: () => void;
}

const ImageView = forwardRef<ImageViewRef, ImageViewProps>(({
  url,
  documentId,
  onFullscreen,
  showControls = true,
  className = "",
  isFullscreenMode = false,
  onLoadComplete,
  onLoadError,
  canDownload = true,
}, ref) => {
  const [imageSrc, setImageSrc] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [rotation, setRotation] = useState(0);

  // Expose functions via ref
  useImperativeHandle(ref, () => ({
    handleRotate,
    handleResetTransform,
    handleDownload,
  }), []);

  useEffect(() => {
    let cancelled = false;
    let currentImageUrl = "";

    const fetchImage = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(url, {
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error("Failed to load image");
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        
        if (!cancelled) {
          currentImageUrl = objectUrl;
          setImageSrc(objectUrl);
          onLoadComplete?.();
        } else {
          URL.revokeObjectURL(objectUrl);
        }
      } catch (error) {
        if (!cancelled) {
          const err = error instanceof Error ? error : new Error("Unknown error");
          setError(err);
          onLoadError?.(err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchImage();

    return () => {
      cancelled = true;
      if (currentImageUrl) {
        URL.revokeObjectURL(currentImageUrl);
      }
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [url]);

  const handleWheel = useCallback((e: WheelEvent) => {
    if (!e.ctrlKey) {
      e.preventDefault();
    }
  }, []);

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleResetTransform = () => {
    setRotation(0);
  };

  const handleDownload = () => {
    if (imageSrc) {
      const a = document.createElement('a');
      a.href = imageSrc;
      a.download = `document-${documentId}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  if (error || (!loading && !imageSrc)) {
    return (
      <div className="flex flex-col w-full h-full">
        <FallbackPreview type="image" message="Immagine non disponibile" />
      </div>
    );
  }

  return (
    <div className={`flex flex-col w-full h-full ${className}`}>
      <div
        className={`flex-1 bg-white relative ${isFullscreenMode ? "h-full" : ""}`}
      >
        <TransformWrapper
          centerOnInit={true}
          minScale={0.5}
          maxScale={3}
          centerZoomedOut={true}
          limitToBounds={true}
          wheel={{
            disabled: false,
            wheelDisabled: false,
            touchPadDisabled: false,
            activationKeys: ["Control"],
          }}
          alignmentAnimation={{ sizeX: 0, sizeY: 0 }}
          panning={{ disabled: false }}
        >
          <TransformComponent
            wrapperClass="w-full h-full flex items-center justify-center"
            contentClass="flex items-center justify-center w-full h-full"
          >
            {loading ? (
              <LoadingSpinner message="Caricamento immagine..." />
            ) : (
              <img
                src={imageSrc}
                alt="Document preview"
                style={{
                  transform: `rotate(${rotation}deg)`,
                  transition: 'transform 0.3s ease',
                }}
                className={`max-w-full object-contain mx-auto ${
                  isFullscreenMode ? "max-h-[calc(100vh-3rem)] max-w-[calc(100vw-1rem)]" : "max-h-full"
                }`}
                onError={() => {
                  const err = new Error("Failed to load image");
                  setError(err);
                  onLoadError?.(err);
                }}
              />
            )}
          </TransformComponent>
        </TransformWrapper>
      </div>

      {showControls && (
        <div className="h-12 bg-gray-50 flex items-center justify-center">
          <ViewerControls 
            onFullscreen={onFullscreen}
            onRotate={isFullscreenMode ? handleRotate : undefined}
            onResetZoom={isFullscreenMode ? handleResetTransform : undefined}
            onDownload={isFullscreenMode ? handleDownload : undefined}
            isFullscreenMode={isFullscreenMode}
            canDownload={canDownload}
          />
        </div>
      )}
    </div>
  );
});

ImageView.displayName = 'ImageView';

export default ImageView;
